<script lang="ts">
	import type { Snippet } from 'svelte'
	import { getContext } from 'svelte'
	import type { Writable } from 'svelte/store'
	import { process_news_badge } from '../badger/utils'
	import type { NewsState, NewsConfig } from '../badger/news.types'

	// TODO: in Svelte 5, use createContext with Types
	const user_state: Writable<NewsState | null> = getContext('user_state')
	const news_config: Writable<NewsConfig> = getContext('news_config')

	type BadgeSnippetProps = {
		text: string
		color?: string
		backgroundColor?: string
		uppercase?: boolean
		large?: boolean
		dataset?: [string, string][]
		classes?: string[]
	}

	interface BadgeProps extends BadgeSnippetProps {
		prefix?: Snippet<[BadgeSnippetProps]>
		suffix?: Snippet<[BadgeSnippetProps]>
	}

	let {
		text,
		color,
		backgroundColor,
		uppercase,
		large,
		dataset = [],
		classes = [],
		prefix,
		suffix,
		...restProps
	}: BadgeProps = $props()

	/**
	 * engineering around race conditions (rendering of badge happens before MetaBroker is done
	 *
	 * in order not to block rendering of routes because of user state fetching in Meta Broker,
	 * we need to make the action here "smart" (reactive effect-like)
	 * so:
	 * - make the processing run only once
	 * - make the processing run only when the user state and news config are available
	 * - use a trick in svelte:actions - In Svelte 4, an action (use:) can return an object with an update method.
	 * - so we pass the user_state (store from context) and wait for it to be available before processing the badge
	 * - every time the store updates, the action re-runs
	 * - if we can process, we process, and set a flag to prevent further processing
	 * - if we can't process, we wait for the next update
	 */

	function processBadgeStateAction(params: {
		user_state: NewsState | null
		news_config: NewsConfig
	}) {
		return (dom: HTMLDivElement) => {
			let processed = false

			function apply({ user_state, news_config }: typeof params) {
				if (processed) return

				const newsItemId = dataset.find(([key]) => key === 'dataNewsId')?.[1]

				if (!newsItemId || !user_state?.news) {
					return
				}

				processed = true

				for (const [key, value] of dataset) {
					dom.dataset[key] = value
				}

				process_news_badge(dom, newsItemId, user_state.news, news_config)
			}

			apply(params)

			return {
				update(newParams: typeof params) {
					apply(newParams)
				}
			}
		}
	}
</script>

<div
	class={['badge', ...classes]}
	class:large
	style:--badge-bg={backgroundColor}
	{...restProps}
	{@attach processBadgeStateAction({ user_state: $user_state, news_config: $news_config })}
>
	{#if prefix}
		{@render prefix({ text, color, backgroundColor, uppercase, large })}
	{/if}
	<span class="badge-text" class:uppercase style:--badge-color={color}>{text}</span>
	{#if suffix}
		{@render suffix?.({ text, color, backgroundColor, uppercase, large })}
	{/if}
</div>

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';

	.badge {
		background-color: var(--badge-wrapper-bg, var(--badge-bg));
		padding: 0.125rem 0.5rem;
		border-radius: 0.25rem;

		@include global.flex-row(0, center, center);

		&.large {
			padding: 0.5rem 0.75rem;
		}
	}

	.badge-text {
		color: var(--badge-wrapper-color, var(--badge-color));
		margin: 0;
		padding: 0;
		font-weight: 500;
		text-align: center;
		line-height: 1.25rem;

		&.uppercase {
			text-transform: uppercase;
		}
	}

	@media screen and (max-width: breakpoints.$xsmall) {
		.badge {
			padding: 0.125rem 0.5rem;

			&.large {
				padding: 0.375rem 0.625rem;
			}
		}

		.badge-text {
			font-size: 0.625rem;
			line-height: 1rem;
		}
	}
</style>
